"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import Image from "next/image";
import BehindTheScenesCard from "@/components/common/section/BehindTheScenesCard";
import eventLeftArrowIcon from "@/assets/home/eventLeftArrowIcon.svg";
import eventRightArrowIcon from "@/assets/home/eventRightArrowIcon.svg";
import "swiper/css";
import "swiper/css/navigation";

function BehindTheScenesCarousel({ cards, readMoreText, prevLabel, nextLabel }) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const items = Array.isArray(cards) ? cards : [];

  if (items.length === 0) return null;

  // With a single event there is nothing to navigate to, so skip Swiper
  // entirely: no arrows, no peeking next card, no right-edge bleed.
  if (items.length === 1) {
    const card = items[0];
    return (
      <div className="flex flex-col w-full">
        <BehindTheScenesCard
          image={card.image}
          label={card.label}
          title={card.title}
          readMoreText={readMoreText}
          route={card.route}
          unoptimized={card.unoptimized}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      {/* Navigation arrows */}
      <div className="flex items-center gap-3 mb-2.5 md:mb-6">
        <button
          ref={prevRef}
          type="button"
          aria-label={prevLabel}
          className="w-6 h-6 md:w-10 md:h-10 rounded-full  flex items-center justify-center transition-colors hover:bg-ff-blue-80 cursor-pointer"
        >
          <Image
            src={eventLeftArrowIcon}
            alt=""
            width={23}
            height={23}
            className="w-5.75 h-5.75 md:w-[clamp(16px,2.0833vw,40px)] md:h-[clamp(16px,2.0833vw,40px)]"
          />
        </button>
        <button
          ref={nextRef}
          type="button"
          aria-label={nextLabel}
          className="w-6 h-6 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors hover:bg-ff-blue-80 cursor-pointer"
        >
          <Image
            src={eventRightArrowIcon}
            alt=""
            width={23}
            height={23}
            className="w-5.75 h-5.75 md:w-[clamp(16px,2.0833vw,40px)] md:h-[clamp(16px,2.0833vw,40px)]"
          />
        </button>
      </div>

      {/* Swiper carousel — a single full card on mobile, one full card plus the
          next peeking in from 768px up, per the design */}
      <Swiper
        modules={[Navigation]}
        spaceBetween={24}
        slidesPerView={1}
        breakpoints={{
          768: { slidesPerView: 1.32, spaceBetween: 32 },
          1280: { slidesPerView: 1.38, spaceBetween: 32 }
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        className="w-full overflow-hidden! md:w-[calc(100%+2rem)] md:-mr-8 lg:w-[calc(100%+2.5rem)] lg:-mr-10 xl:w-[calc(100%+4rem)] xl:mr-7.5"
      >
        {items.map((card, index) => (
          <SwiperSlide key={index} className="h-auto mr-7.5">
            <BehindTheScenesCard
              image={card.image}
              label={card.label}
              title={card.title}
              readMoreText={readMoreText}
              route={card.route}
              unoptimized={card.unoptimized}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default BehindTheScenesCarousel;
